import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthContext';
import { Link } from '@tanstack/react-router';
import { CheckCircle } from 'lucide-react';
import { sanitizePhoneInput } from '@/lib/utils';

export function TenantRegisterPage() {
  const { t } = useTranslation();
  const { registerTenant } = useAuth();
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError(t('auth.passwordsDoNotMatch', 'Passwords do not match'));
      return;
    }
    if (form.password.length < 8) {
      setError(t('auth.passwordMinLength', 'Password must be at least 8 characters'));
      return;
    }

    setLoading(true);
    try {
      await registerTenant(form.email || undefined, form.password, form.fullName, form.phone);
      setRegistered(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.registrationFailed', 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  if (registered) {
    const hasEmail = !!form.email.trim();
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-10 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          {hasEmail ? (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.accountCreated', 'Check Your Email!')}</h2>
              <p className="text-gray-600 mb-2">We sent a verification link to your email. <strong>Please verify your email to activate your account.</strong></p>
              <p className="text-sm text-gray-500 mb-8">
                Check your inbox (and spam folder). Once verified, you can log in. If your hostel has you on file with this email, your data will appear automatically.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.accountCreatedNoEmail', 'Account Created!')}</h2>
              <p className="text-gray-600 mb-8">
                {t('auth.accountCreatedNoEmailNote', 'You can log in now with your phone number and password. If your hostel has you on file with this phone number, your data will appear automatically.')}
              </p>
            </>
          )}
          <Link
            to="/login"
            className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-2.5 rounded-lg transition"
          >
            {t('auth.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('auth.tenantSignUpTitle', 'Tenant Sign Up')}</h1>
          <p className="text-gray-500 mt-2">{t('auth.tenantSignUpSubtitle', 'Create your account to view payments and notifications')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.fullName')} *</label>
              <input type="text" required value={form.fullName} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} className={inp} placeholder="John Doe" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.phone')} *</label>
              <input type="tel" inputMode="tel" required value={form.phone} onChange={e => setForm(f => ({ ...f, phone: sanitizePhoneInput(e.target.value) }))} className={inp} placeholder="+91 9999999999" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.email')} ({t('common.optional', 'optional')})</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inp} placeholder="you@example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.password')} *</label>
              <input type="password" required value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className={inp} placeholder="8+ characters" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.confirmPassword')} *</label>
              <input type="password" required value={form.confirmPassword} onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))} className={inp} placeholder="Repeat password" />
            </div>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? t('auth.creatingAccount', 'Creating Account…') : t('auth.createAccount', 'Create Account')}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-teal-600 font-medium hover:underline">{t('auth.signIn')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
